import React from "react";
import { X } from "lucide-react";
import { useSet } from "react-use";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BusinessReport } from "@/modules/snapshots/businessReports.types";
import { ExpandCollapseButton } from "./components/ExpandCollapseButton";
import { PromptItem } from "./components/PromptItem";

type BusinessReportPromptsModalProps = {
  businessReport: BusinessReport;
  onClose: () => void;
};

export const BusinessReportPromptListModal: React.FC<BusinessReportPromptsModalProps> = ({
  businessReport,
  onClose,
}) => {
  const [expandedItems, { toggle, clear, add }] = useSet<string>(new Set());

  const isAllExpanded = businessReport.prompts.every(p => expandedItems.has(p.id));

  const toggleAll = React.useCallback(() => {
    if (isAllExpanded) {
      clear();
    } else {
      businessReport.prompts.forEach(p => add(p.id));
    }
  }, [businessReport.prompts, clear, add, isAllExpanded]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] overflow-hidden rounded-2xl p-0 md:max-w-[800px] xl:max-w-[900px] 2xl:max-w-[1200px]">
        <div className="bg-white px-6 pb-2 pt-6 sm:px-8 md:px-10 2xl:px-14">
          <DialogHeader>
            <DialogTitle>{businessReport.name}</DialogTitle>
          </DialogHeader>

          <div className="mb-6 mt-2 flex items-center justify-between px-4">
            <h3 className="text-lg font-semibold">Topics</h3>
            <ExpandCollapseButton isExpanded={isAllExpanded} onClick={toggleAll} />
          </div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto bg-white px-6 sm:px-8 md:px-10 2xl:px-14">
          {businessReport.prompts.map(prompt => (
            <PromptItem
              key={prompt.id}
              prompt={prompt}
              isExpanded={expandedItems.has(prompt.id)}
              onToggle={() => toggle(prompt.id)}
            />
          ))}
        </div>

        <div className="-mr-2 flex justify-end gap-3 border-t border-gray-100 bg-white px-6 sm:px-8 md:px-10 2xl:px-14">
          <Button variant="outline" onClick={onClose} className="my-4 mr-6 gap-2">
            <X size={16} />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
